import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useRef, useState, useMemo } from 'react';
import {
  Stack,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableRowsIcon from '@mui/icons-material/TableRows';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import dayjs from 'dayjs';

import {
  CrossEntityDateCrudDataGrid,
  ValueWithEntityReference,
  CrossEntityDateCrudDataGridHandle,
  ValuePropConfig,
} from '../../CrossEntityDateCrudDataGrid';
import {
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisReadDTO,
  DiagnosisConsumptionReadDTO,
  DiagnosisConsumptionDataReadDTO,
  DiagnosisMetersReadDTO,
  DiagnosisRelevantVariablesReadDTO,
  MetersDataReadDTO,
  VariablesDataReadDTO,
  ConsumptionType,
  EnergyVectorsReadDTO,
  ConsumptionTotalsCreateDTO,
} from '../../../generated/api-client';
import { apiClient, axiosInstance } from '../../../services/apiClient';
import { getAxiosErrorDetail } from '../../../utils/axiosErrorDetail';
import { DiagnosisStepHandle } from './Step.types';
import {
  useDiagnosisEnergyVectorsByDiagnosisId,
  useDiagnosisMetersByDiagnosisId,
  useDiagnosisRelevantVariablesByDiagnosisId,
  useConsumptionTotalsByConsumptionIds,
  useCreateConsumptionTotal,
  useUpdateConsumptionTotal,
  invalidateConsumptionTotalsQueries,
} from '../../../hooks';
import { formatNumberItalian } from '../../../utils/numberFormat';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { EditConsumptionDialog } from './EditConsumptionDialog';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ConsumptionCostsChartsSection } from './ConsumptionCostsChartsSection';
import { GridRowId } from '@mui/x-data-grid';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';
import {
  buildReferencePeriodYearlyValues,
  type SourceValueItem,
} from '../../../utils/referencePeriodYearlyValues';
import { ActionSpacer } from '../../common';

type ViewMode = 'table' | 'charts';
interface DiagnosisConsumptionCostsStepProps {
  diagnosisId?: string;
  diagnosis?: DiagnosisReadDTO | null;
}

/**
 * Costo unitario = imponibile / quantità, solo se la quantità è valorizzata (> 0).
 * Centralizza la regola usata in più punti (init tabella, modifica cella "Imponibile",
 * periodo di riferimento) per evitare che i vari calcoli divergano fra loro.
 */
function computeUnitCost(
  taxableAmount: number | null | undefined,
  quantity: number | null | undefined
): number | null {
  if (taxableAmount === null || taxableAmount === undefined) return null;
  const p = Number(taxableAmount);
  const q = Number(quantity ?? 0);
  if (!Number.isFinite(p) || !Number.isFinite(q) || !q) return null;
  return p / q;
}

// Tipo per i dati di consumo e costo
interface ConsumptionCostData {
  id?: string; // ID del record di ConsumptionTotals
  diagnosis_consumption_id?: string;
  year?: number;
  quantity: number;
  taxable_amount: number | null;
  unit_cost: number | null;
  unit_of_measurement?: string;
}

export const DiagnosisConsumptionCostsStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisConsumptionCostsStepProps>(
    ({ diagnosisId, diagnosis }, ref) => {
      const { readOnly } = useDiagnosisPermission();
      const queryClient = useQueryClient();
      const crossEntityGridRef = useRef<CrossEntityDateCrudDataGridHandle>(null);
      const consumptionGridRef = useRef<CrossEntityDateCrudDataGridHandle>(null);
      const crossEntityGridRefPeriodoRiferimento = useRef<CrossEntityDateCrudDataGridHandle>(null);

      const referencePeriodStart = new Date(diagnosis?.reference_period_from!);
      const referencePeriodEnd = new Date(diagnosis?.reference_period_to!);

      const [editConsumptionDialogOpen, setEditConsumptionDialogOpen] = useState(false);
      const [selectedConsumption, setSelectedConsumption] =
        useState<DiagnosisConsumptionReadDTO | null>(null);
      const [deleteConsumptionDialogState, setDeleteConsumptionDialogState] = React.useState<{
        open: boolean;
        id: string | null;
      }>({ open: false, id: null });
      const [viewMode, setViewMode] = useState<ViewMode>('table');
      const hasGeneratedPeriod = Boolean(
        diagnosis?.data_generated_from && diagnosis?.data_generated_to
      );

      // Carica i vettori energetici della diagnosi usando DiagnosisEnergyVectorsApi
      const { data: energyVectors = [], isLoading: isLoadingVectors } =
        useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId!);

      // Memoizza gli ID dei vettori energetici per chiavi di query stabili
      const energyVectorIds = useMemo(
        () => energyVectors.map(v => v.id).filter(Boolean) as string[],
        [energyVectors]
      );

      // Carica i vettori energetici generici per ottenere unit_of_measurement e nome
      const { data: genericEnergyVectors = [] } = useQuery<EnergyVectorsReadDTO[]>(
        'generic-energy-vectors',
        () => apiClient.list_items_energy_vectors__get(),
        { enabled: true }
      );

      // Carica i consumi per tutti i vettori energetici
      const { data: allConsumptions = [], isLoading: isLoadingConsumptions } = useQuery<
        DiagnosisConsumptionReadDTO[]
      >(
        [`all-consumptions-${diagnosisId}`],
        async () => {
          if (!energyVectors.length) return [];
          const consumptions: DiagnosisConsumptionReadDTO[] = [];
          for (const vector of energyVectors) {
            if (vector.id) {
              try {
                const vectorConsumptions =
                  await apiClient.get_by_energy_vector_id_diagnosis_consumption_by_energy_vector__energy_vector_id__get(
                    vector.id
                  );
                consumptions.push(...vectorConsumptions);
              } catch (error) {
                console.error(`Errore nel caricamento consumi per vettore ${vector.id}:`, error);
              }
            }
          }
          return consumptions;
        },
        { enabled: !!diagnosisId && energyVectorIds.length > 0 }
      );

      // Posizione assoluta scroll orizzontale (valore unico per tutte e tre le tabelle)
      const [horizontalScrollLeft, setHorizontalScrollLeft] = React.useState(0);
      const setScrollPos = React.useCallback((pos: number) => {
        setHorizontalScrollLeft(prev => (prev === pos ? prev : pos));
      }, []);

      // Shared column order state for synchronizing both tables
      const [sharedColumnOrder, setSharedColumnOrder] = React.useState<string[]>([]);

      // Initialize shared column order from allConsumptions when they're loaded
      React.useEffect(() => {
        if (allConsumptions.length > 0 && sharedColumnOrder.length === 0) {
          const entityFields = allConsumptions.map(c => `entity-${c.id}`).filter(Boolean);
          setSharedColumnOrder(entityFields);
        }
      }, [allConsumptions, sharedColumnOrder.length]);

      // Mutation for updating consumption order
      const updateOrderMutation = useMutation(
        async (consumptionIds: string[]) => {
          try {
            const { data } = await axiosInstance.post(
              '/diagnosis-consumption/update_order',
              { consumption_ids: consumptionIds }
            );
            return data;
          } catch (e: unknown) {
            throw new Error(
              getAxiosErrorDetail(e, "Errore nel salvataggio dell'ordinamento")
            );
          }
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
            toast.success('Ordinamento consumi salvato con successo');
          },
          onError: (error: any) => {
            toast.error(error?.message || "Errore nel salvataggio dell'ordinamento dei consumi");
          },
        }
      );

      const consumptionIds = useMemo(
        () =>
          allConsumptions
            .map(consumption => consumption.id)
            .filter((id): id is string => Boolean(id)),
        [allConsumptions]
      );

      const {
        data: consumptionTotals = [],
        isLoading: isLoadingConsumptionTotals,
        refetch: refetchConsumptionTotals,
      } = useConsumptionTotalsByConsumptionIds(diagnosisId, consumptionIds);

      const { mutateAsync: createConsumptionTotal } = useCreateConsumptionTotal(diagnosisId);
      const { mutateAsync: updateConsumptionTotal } = useUpdateConsumptionTotal(diagnosisId);

      // Carica i dati mensili dei consumi
      const { data: consumptionDataValues = [], isLoading: isLoadingConsumptionData } = useQuery<
        ValueWithEntityReference<DiagnosisConsumptionDataReadDTO>[]
      >(
        [`all-consumption-data-${diagnosisId}`],
        async () => {
          if (!allConsumptions.length) return [];
          const allData: DiagnosisConsumptionDataReadDTO[] = [];
          for (const consumption of allConsumptions) {
            if (consumption.id) {
              try {
                const data =
                  await apiClient.get_by_consumption_id_diagnosis_consumption_data_by_consumption__consumption_id__get(
                    consumption.id
                  );
                allData.push(...data);
              } catch (error) {
                console.error(`Errore nel caricamento dati consumo ${consumption.id}:`, error);
              }
            }
          }
          return allData.map(data => ({
            value: data,
            entityId: data.diagnosis_consumption_id ?? '',
            month: data.month ?? new Date(),
          }));
        },
        { enabled: !!diagnosisId && consumptionIds.length > 0 }
      );

      // Contatori (meters) e loro dati per i grafici
      const { data: meters = [] } = useDiagnosisMetersByDiagnosisId(diagnosisId!);
      const metersFiltered = useMemo(
        () => meters.filter((m: DiagnosisMetersReadDTO) => !(m as any).is_fake),
        [meters]
      );
      const { data: allMetersData = [] } = useQuery<MetersDataReadDTO[]>(
        [`all-meters-data-chart-${diagnosisId}`, metersFiltered.map(m => m.id)],
        async () => {
          if (!metersFiltered.length) return [];
          const out: MetersDataReadDTO[] = [];
          for (const meter of metersFiltered) {
            if (meter.id) {
              const data =
                await apiClient.search_by_meter_id_meters_data_search_by_meter_id__meter_id__get(
                  meter.id
                );
              out.push(...data);
            }
          }
          return out;
        },
        { enabled: !!diagnosisId && metersFiltered.length > 0 }
      );

      // Variabili pertinenti e loro dati per i grafici
      const { data: relevantVariables = [] } = useDiagnosisRelevantVariablesByDiagnosisId(
        diagnosisId!
      );
      const { data: allVariablesData = [] } = useQuery<VariablesDataReadDTO[]>(
        [`all-variables-data-chart-${diagnosisId}`, relevantVariables.map(v => v.id)],
        async () => {
          if (!relevantVariables.length) return [];
          const out: VariablesDataReadDTO[] = [];
          for (const variable of relevantVariables) {
            if (variable.id) {
              const data =
                await apiClient.search_by_variable_id_variables_data_search_by_variable_id__variable_id__get(
                  variable.id
                );
              out.push(...data);
            }
          }
          return out;
        },
        { enabled: !!diagnosisId && relevantVariables.length > 0 }
      );

      // Stato locale per i dati (inizializzato con mock data)
      const [valuesData, setValuesData] = useState<ValueWithEntityReference<ConsumptionCostData>[]>(
        []
      );
      const initialValuesRef = React.useRef<ValueWithEntityReference<ConsumptionCostData>[]>([]);

      // Memoizza chiavi stabili per le dipendenze dell'useEffect
      const consumptionTotalsKey = useMemo(
        () =>
          consumptionTotals.map(t => `${t.id}-${t.year}-${t.diagnosis_consumption_id}`).join('|'),
        [consumptionTotals]
      );

      const allConsumptionsKey = useMemo(
        () =>
          allConsumptions.map(c => `${c.id}-${c.diagnosis_energy_vector_id}-${c.type}`).join('|'),
        [allConsumptions]
      );

      const energyVectorsKey = useMemo(
        () => energyVectors.map(v => `${v.id}-${v.unit_of_measurement}`).join('|'),
        [energyVectors]
      );

      // Inizializza i dati della tabella dei totali in base ai dati caricati dal backend
      React.useEffect(() => {
        if (!hasGeneratedPeriod || allConsumptions.length === 0) {
          setValuesData([]);
          initialValuesRef.current = [];
          return;
        }

        const fromYear = dayjs(diagnosis?.data_generated_from!).year();
        const toYear = dayjs(diagnosis?.data_generated_to!).year();
        const years: number[] = [];
        for (let year = fromYear; year <= toYear; year++) {
          years.push(year);
        }

        const totalsByConsumption = new Map<string, Map<number, any>>();
        consumptionTotals.forEach(total => {
          if (!total.diagnosis_consumption_id) {
            return;
          }
          if (!totalsByConsumption.has(total.diagnosis_consumption_id)) {
            totalsByConsumption.set(total.diagnosis_consumption_id, new Map());
          }
          totalsByConsumption.get(total.diagnosis_consumption_id)!.set(total.year, total);
        });

        const vectorMap = new Map<string, DiagnosisEnergyVectorsReadDTO>();
        energyVectors.forEach(vector => {
          if (vector.id) {
            vectorMap.set(vector.id, vector);
          }
        });

        // Fallback quantità: se il totale annuo (`ConsumptionTotals.quantity`, calcolato lato
        // backend) non è disponibile o è 0 per un anno, somma direttamente i dati mensili per
        // quell'anno. Senza questo fallback, un disallineamento fra i totali annui e i dati
        // mensili effettivamente presenti (es. subito dopo l'inserimento di nuovi contatori)
        // lascia il costo unitario vuoto pur avendo inserito l'imponibile, perché non si può
        // dividere per una quantità pari a zero.
        const monthlyQuantityByConsumptionYear = new Map<string, number>();
        consumptionDataValues.forEach(item => {
          if (!item.entityId) return;
          const monthValue = (item.value as any)?.value;
          if (monthValue === null || monthValue === undefined) return;
          const monthDate = item.month ?? (item.value as any)?.month;
          if (!monthDate) return;
          const year = dayjs(monthDate).year();
          const key = `${item.entityId}-${year}`;
          monthlyQuantityByConsumptionYear.set(
            key,
            (monthlyQuantityByConsumptionYear.get(key) ?? 0) + Number(monthValue)
          );
        });

        const data: ValueWithEntityReference<ConsumptionCostData>[] = [];

        allConsumptions.forEach(consumption => {
          if (!consumption.id) {
            return;
          }
          const consumptionId = consumption.id;
          const unitOfMeasurement =
            (consumption as any)?.unit_of_measurement ||
            vectorMap.get(consumption.diagnosis_energy_vector_id || '')?.unit_of_measurement ||
            'kWh';

          years.forEach(year => {
            const totalsForConsumption = consumptionId
              ? totalsByConsumption.get(consumptionId)
              : undefined;
            const record = totalsForConsumption?.get(year);

            let quantity =
              record?.quantity !== undefined && record?.quantity !== null
                ? Number(record.quantity)
                : 0;
            if (!quantity) {
              quantity = monthlyQuantityByConsumptionYear.get(`${consumptionId}-${year}`) ?? 0;
            }
            const price =
              record?.price !== undefined && record?.price !== null ? Number(record.price) : null;
            let unitCost =
              record?.unit_cost !== undefined && record?.unit_cost !== null
                ? Number(record.unit_cost)
                : null;

            // Un costo unitario salvato esattamente a 0 pur avendo imponibile e quantità validi
            // è quasi certamente un residuo di un calcolo precedente (es. fatto quando la
            // quantità era ancora 0): va trattato come "da ricalcolare", non come valore reale.
            const unitCostLooksStale = unitCost === 0 && price != null && price !== 0 && quantity > 0;

            if ((unitCost === null || unitCost === undefined || unitCostLooksStale) && price !== null) {
              const computed = computeUnitCost(price, quantity);
              if (computed !== null) {
                unitCost = computed;
              }
            }

            data.push({
              value: {
                id: record?.id,
                diagnosis_consumption_id: consumptionId,
                year,
                quantity,
                taxable_amount: price,
                unit_cost: unitCost,
                unit_of_measurement: unitOfMeasurement,
              },
              entityId: consumptionId,
              date: new Date(year, 0, 1),
            });
          });
        });

        setValuesData(data);
        initialValuesRef.current = data.map(item => ({
          ...item,
          date: new Date(item.date as any),
          value: item.value ? { ...item.value } : item.value,
        }));
      }, [
        hasGeneratedPeriod,
        diagnosis?.data_generated_from,
        diagnosis?.data_generated_to,
        allConsumptionsKey,
        consumptionTotalsKey,
        energyVectorsKey,
        allConsumptions,
        consumptionTotals,
        energyVectors,
        consumptionDataValues,
      ]);

      // Periodo di riferimento: usa reference_period_* da backend (diagnosis_consumption) se presenti,
      // altrimenti ricalcola da dati mensili + valuesData
      const valuesDataReferencePeriod = useMemo(() => {
        if (!diagnosis?.reference_period_from || !diagnosis?.reference_period_to) return [];
        const refFromYear = dayjs(diagnosis.reference_period_from).year();
        const refDate = new Date(refFromYear, 0, 1);
        const fromBackend = allConsumptions.some(
          (c: any) =>
            c.reference_period_quantity != null ||
            c.reference_period_price != null ||
            c.reference_period_unitcost != null
        );
        if (fromBackend) {
          return allConsumptions
            .filter((c: any) => c.id)
            .map((c: any) => ({
              entityId: c.id,
              date: refDate,
              value: {
                diagnosis_consumption_id: c.id,
                year: refFromYear,
                quantity: Number(c.reference_period_quantity) ?? 0,
                taxable_amount:
                  c.reference_period_price != null ? Number(c.reference_period_price) : null,
                unit_cost:
                  c.reference_period_unitcost != null ? Number(c.reference_period_unitcost) : null,
                unit_of_measurement: c.unit_of_measurement ?? 'kWh',
              },
            }));
        }
        // Fallback: solo quantità dal periodo (dati mensili). Imponibile e costo unitario
        // non vanno presi dalla tabella annua: restano null finché il backend non fornisce reference_period_*
        if (!consumptionDataValues.length) return [];
        const entityIds = allConsumptions.map(c => c.id).filter(Boolean) as string[];
        const unitOfMeasurementByEntity = new Map<string, string>();
        allConsumptions.forEach((c: any) => {
          if (c.id && c.unit_of_measurement)
            unitOfMeasurementByEntity.set(c.id, c.unit_of_measurement);
        });
        return buildReferencePeriodYearlyValues({
          sourceValues: consumptionDataValues as SourceValueItem[],
          periodFrom: diagnosis.reference_period_from,
          periodTo: diagnosis.reference_period_to,
          entityIds,
          getValueToSum: item => Number((item.value as any)?.value ?? 0),
          buildValue: (entityId, _year, sumQuantity, _count, _ctx) => ({
            diagnosis_consumption_id: entityId,
            year: refFromYear,
            quantity: sumQuantity,
            taxable_amount: null,
            unit_cost: null,
            unit_of_measurement: unitOfMeasurementByEntity.get(entityId) ?? 'kWh',
          }),
        });
      }, [
        consumptionDataValues,
        allConsumptions,
        diagnosis?.reference_period_from,
        diagnosis?.reference_period_to,
      ]);

      // Funzione helper per aggiornare un valore nei valuesData
      const updateValueInData = React.useCallback(
        (
          entityId: string,
          date: Date,
          updater: (currentValue: ConsumptionCostData | null) => ConsumptionCostData
        ) => {
          setValuesData(prev => {
            const dateKey = dayjs(date).startOf('year').valueOf();
            return prev.map(item => {
              const itemDateKey = dayjs(item.date).startOf('year').valueOf();
              if (item.entityId === entityId && itemDateKey === dateKey) {
                return {
                  ...item,
                  value: updater(item.value),
                };
              }
              return item;
            });
          });
        },
        []
      );

      // Configurazione delle proprietà dei valori complessi
      const valuesProps: ValuePropConfig<ConsumptionCostData>[] = useMemo(() => {
        return [
          {
            field: 'quantity',
            label: 'Quantità',
            isPrimary: false,
            editable: false,
            decimalPlaces: DECIMAL_PLACES.consumi_costi_quantita,
            getValue: valueObj => valueObj?.quantity ?? 0,
            setValue: (valueObj, newValue) => {
              // Quantità non modificabile, ritorna invariato
              return valueObj || { quantity: 0, taxable_amount: null, unit_cost: null };
            },
          },
          {
            field: 'taxable_amount',
            label: 'Imponibile',
            isPrimary: true, // Valore principale per copy/paste
            editable: true,
            decimalPlaces: DECIMAL_PLACES.consumi_costi_imponibile,
            getValue: valueObj => {
              // Restituisce il valore dell'imponibile dal valueObj
              return valueObj?.taxable_amount ?? null;
            },
            setValue: (valueObj, newValue) => {
              // setValue viene chiamato dalla cross table, ma la logica di calcolo
              // è gestita in processRowUpdate che aggiorna anche valuesData
              // Qui restituiamo solo un valueObj con il valore inserito, senza calcoli
              const updated = {
                ...(valueObj || { quantity: 0, taxable_amount: null, unit_cost: null }),
              };
              const numValue = typeof newValue === 'number' ? newValue : Number(newValue);
              if (!isNaN(numValue) && numValue !== null && numValue.toString() !== '') {
                updated.taxable_amount = numValue;
              } else {
                updated.taxable_amount = null;
              }
              return updated;
            },
          },
          {
            field: 'unit_cost',
            label: 'Costo unitario',
            isPrimary: false,
            editable: true,
            decimalPlaces: DECIMAL_PLACES.consumi_costi_costo_unitario,
            getValue: valueObj => valueObj?.unit_cost ?? null,
            setValue: (valueObj, newValue) => {
              // setValue viene chiamato dalla cross table, ma la logica di calcolo
              // è gestita in processRowUpdate che aggiorna anche valuesData
              const updated = {
                ...(valueObj || { quantity: 0, taxable_amount: null, unit_cost: null }),
              };
              const numValue = typeof newValue === 'number' ? newValue : Number(newValue);
              if (!isNaN(numValue) && numValue !== null && numValue.toString() !== '') {
                updated.unit_cost = numValue;
              } else {
                updated.unit_cost = null;
              }
              return updated;
            },
          },
        ];
      }, []);

      // Render personalizzato per la colonna "Dato"
      const renderDataColumnCell = React.useCallback((params: { row: any }) => {
        const row = params.row;
        const valuePropIndex = row._valuePropIndex as number;
        const valuePropLabel = row._valuePropLabel as string;

        // Mostra "Udm" per Quantità, "Euro" per Imponibile e "Euro/Udm" per Costo unitario
        let unitLabel = '';
        if (valuePropIndex === 0) {
          // Quantità: mostra "Udm"
          unitLabel = ' [Udm]';
        } else if (valuePropIndex === 1) {
          // Imponibile: mostra "Euro"
          unitLabel = ' [Euro]';
        } else if (valuePropIndex === 2) {
          // Costo unitario: mostra "Euro/Udm"
          unitLabel = ' [Euro/Udm]';
        }

        return (
          <Box sx={{ width: '100%' }}>
            {valuePropLabel}
            {unitLabel}
          </Box>
        );
      }, []);

      // Render personalizzato per le celle valore (Quantità/Imponibile/Costo unitario): identico
      // alla formattazione di default della griglia, con l'aggiunta di un suggerimento quando il
      // costo unitario risulta vuoto perché la quantità dell'anno è 0 (non è un valore mancante
      // per errore, ma un calcolo impossibile: non si può dividere per zero).
      const renderConsumptionValueCell = React.useCallback(
        (params: {
          value: number | string | null | undefined;
          valueObj: ConsumptionCostData | null;
          valueProp?: ValuePropConfig<ConsumptionCostData>;
        }) => {
          const { value, valueObj, valueProp } = params;
          const fmtOpts =
            valueProp?.decimalPlaces != null
              ? {
                  maximumFractionDigits: valueProp.decimalPlaces,
                  minimumFractionDigits: valueProp.decimalPlaces,
                }
              : undefined;
          const isEmpty = value === null || value === undefined || value === '';
          const quantityIsZero = !valueObj?.quantity || Number(valueObj.quantity) === 0;

          if (valueProp?.field === 'unit_cost' && isEmpty && quantityIsZero) {
            return (
              <Tooltip title="Quantità pari a 0 per questo anno: il costo unitario non è calcolabile" arrow>
                <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  —
                </Box>
              </Tooltip>
            );
          }

          return (
            <>{!isEmpty ? formatNumberItalian(value as any, fmtOpts) : (value ?? '')}</>
          );
        },
        []
      );

      // Process row update per calcolo automatico e aggiornamento valuesData
      const processRowUpdate = React.useCallback(
        async (newRow: any, oldRow: any): Promise<any> => {
          // Trova quale campo è stato modificato
          const changedField = Object.keys(newRow).find(
            key => key.startsWith('entity-') && newRow[key] !== oldRow[key]
          );

          if (!changedField) {
            return newRow;
          }

          // Estrai entityId dal nome del campo
          const entityId = changedField.replace('entity-', '');
          const newValue = newRow[changedField];
          const valuePropIndex = newRow._valuePropIndex as number;
          const date = newRow._date as Date;

          // Trova il valueObj corrente
          const valueObjKey = `_entity-${entityId}-valueObj`;
          const currentValueObj = (newRow[valueObjKey] as ConsumptionCostData) || {
            quantity: 0,
            taxable_amount: null,
            unit_cost: null,
            unit_of_measurement: 'kWh',
            diagnosis_consumption_id: undefined,
            year: dayjs(date).year(),
          };

          // Applica la logica di calcolo in base al campo modificato
          // IMPORTANTE: preserva il valore originale inserito dall'utente
          let updatedValueObj = { ...currentValueObj };

          const quantityValue = Number(updatedValueObj.quantity ?? 0);

          if (valuePropIndex === 1) {
            // Imponibile modificato: mantieni il valore inserito (newValue) e calcola solo costo unitario
            const numValue = typeof newValue === 'number' ? newValue : Number(newValue);
            if (!isNaN(numValue) && numValue !== null && numValue.toString() !== '') {
              // Mantieni il valore inserito dall'utente
              updatedValueObj.taxable_amount = numValue;
              // Calcola solo il costo unitario (stessa regola centralizzata usata nell'init)
              updatedValueObj.unit_cost = computeUnitCost(numValue, quantityValue);
            } else {
              updatedValueObj.taxable_amount = null;
              updatedValueObj.unit_cost = null;
            }
          } else if (valuePropIndex === 2) {
            // Costo unitario modificato: mantieni il valore inserito (newValue) e calcola solo imponibile
            const numValue = typeof newValue === 'number' ? newValue : Number(newValue);
            if (!isNaN(numValue) && numValue !== null && numValue.toString() !== '') {
              // Mantieni il valore inserito dall'utente
              updatedValueObj.unit_cost = numValue;
              // Calcola solo l'imponibile
              if (quantityValue > 0) {
                updatedValueObj.taxable_amount = quantityValue * numValue;
              } else {
                updatedValueObj.taxable_amount = null;
              }
            } else {
              updatedValueObj.unit_cost = null;
              updatedValueObj.taxable_amount = null;
            }
          }

          // Aggiorna i valuesData (sorgente principale) - questo farà ri-renderizzare la grid
          updateValueInData(entityId, date, () => updatedValueObj);

          // Aggiorna il valueObj nella riga
          newRow[valueObjKey] = updatedValueObj;

          // Aggiorna anche i valori nella riga corrente per gli altri valueProps
          // IMPORTANTE: non sovrascrivere il campo appena modificato dall'utente
          valuesProps.forEach((valueProp, idx) => {
            if (idx !== valuePropIndex && valueProp.getValue) {
              const fieldName = `entity-${entityId}`;
              const value = valueProp.getValue(updatedValueObj);
              newRow[fieldName] = value;
            }
          });

          // Assicurati che il campo modificato mantenga il valore originale inserito
          // (non quello calcolato da getValue)
          newRow[changedField] = newValue;

          return newRow;
        },
        [valuesProps, updateValueInData]
      );

      // Stato dedicato alla tabella "Periodo di riferimento": modifiche qui non toccano la tabella annua
      const [refPeriodValues, setRefPeriodValues] = useState<
        ValueWithEntityReference<ConsumptionCostData>[]
      >([]);
      React.useEffect(() => {
        if (valuesDataReferencePeriod.length > 0) {
          setRefPeriodValues(valuesDataReferencePeriod);
        }
      }, [valuesDataReferencePeriod]);

      // Confronto numeri o null (con tolleranza per float)
      const refPeriodValueChanged = (
        current: number | null | undefined,
        initial: number | null | undefined
      ): boolean => {
        const a = current != null && !Number.isNaN(Number(current)) ? Number(current) : null;
        const b = initial != null && !Number.isNaN(Number(initial)) ? Number(initial) : null;
        if (a === null && b === null) return false;
        if (a === null || b === null) return true;
        return Math.abs(a - b) > 1e-9;
      };

      // Salva le modifiche della tabella periodo di riferimento sui campi reference_period_* di DiagnosisConsumption.
      // Chiama PATCH solo per i consumi i cui valori sono effettivamente cambiati rispetto a valuesDataReferencePeriod.
      const saveReferencePeriodChanges = React.useCallback(async (): Promise<boolean> => {
        if (refPeriodValues.length === 0) return false;
        const initialByEntityId = new Map(
          valuesDataReferencePeriod.map(item => [item.entityId, item.value as ConsumptionCostData])
        );
        let hasChanges = false;
        for (const item of refPeriodValues) {
          if (!item.entityId || !item.value) continue;
          const v = item.value as ConsumptionCostData;
          const initial = initialByEntityId.get(item.entityId);
          const qtyChanged = refPeriodValueChanged(v.quantity, initial?.quantity ?? 0);
          const priceChanged = refPeriodValueChanged(
            v.taxable_amount,
            initial?.taxable_amount ?? null
          );
          const unitCostChanged = refPeriodValueChanged(v.unit_cost, initial?.unit_cost ?? null);
          if (!qtyChanged && !priceChanged && !unitCostChanged) continue;
          const payload: Record<string, number | null> = {};
          if (qtyChanged) payload.reference_period_quantity = Number(v.quantity);
          if (priceChanged)
            payload.reference_period_price =
              v.taxable_amount != null ? Number(v.taxable_amount) : null;
          if (unitCostChanged)
            payload.reference_period_unitcost = v.unit_cost != null ? Number(v.unit_cost) : null;
          if (Object.keys(payload).length === 0) continue;
          try {
            await axiosInstance.patch(
              `/diagnosis-consumption/${item.entityId}/reference_period`,
              payload
            );
            hasChanges = true;
          } catch (e) {
            console.error('Save reference period for consumption', item.entityId, e);
            throw e;
          }
        }
        return hasChanges;
      }, [refPeriodValues, valuesDataReferencePeriod]);

      // processRowUpdate solo per la tabella periodo di riferimento: aggiorna refPeriodValues, non valuesData
      const processRowUpdateRefPeriod = React.useCallback(
        async (newRow: any, oldRow: any): Promise<any> => {
          const changedField = Object.keys(newRow).find(
            key => key.startsWith('entity-') && newRow[key] !== oldRow[key]
          );
          if (!changedField) return newRow;
          const entityId = changedField.replace('entity-', '');
          const newValue = newRow[changedField];
          const valuePropIndex = newRow._valuePropIndex as number;
          const valueObjKey = `_entity-${entityId}-valueObj`;
          const currentValueObj = (newRow[valueObjKey] as ConsumptionCostData) || {
            quantity: 0,
            taxable_amount: null,
            unit_cost: null,
            unit_of_measurement: 'kWh',
            diagnosis_consumption_id: entityId,
            year: dayjs(newRow._date).year(),
          };
          let updatedValueObj = { ...currentValueObj };
          const quantityValue = Number(updatedValueObj.quantity ?? 0);
          if (valuePropIndex === 1) {
            const numValue = typeof newValue === 'number' ? newValue : Number(newValue);
            if (!isNaN(numValue) && numValue !== null && numValue.toString() !== '') {
              updatedValueObj.taxable_amount = numValue;
              updatedValueObj.unit_cost = computeUnitCost(numValue, quantityValue);
            } else {
              updatedValueObj.taxable_amount = null;
              updatedValueObj.unit_cost = null;
            }
          } else if (valuePropIndex === 2) {
            const numValue = typeof newValue === 'number' ? newValue : Number(newValue);
            if (!isNaN(numValue) && numValue !== null && numValue.toString() !== '') {
              updatedValueObj.unit_cost = numValue;
              updatedValueObj.taxable_amount = quantityValue > 0 ? quantityValue * numValue : null;
            } else {
              updatedValueObj.unit_cost = null;
              updatedValueObj.taxable_amount = null;
            }
          }
          newRow[valueObjKey] = updatedValueObj;
          valuesProps.forEach((valueProp, idx) => {
            if (idx !== valuePropIndex && valueProp.getValue) {
              newRow[`entity-${entityId}`] = valueProp.getValue(updatedValueObj);
            }
          });
          // Mantieni il valore inserito dall'utente (non sovrascrivere con getValue)
          newRow[changedField] = newValue;
          newRow[`entity-${entityId}`] = newValue;
          setRefPeriodValues(prev =>
            prev.map(p =>
              p.entityId === entityId ? { ...p, value: updatedValueObj as ConsumptionCostData } : p
            )
          );
          return newRow;
        },
        [valuesProps]
      );

      // Etichette come nelle tabelle contatori e variabili (stesso encoding delle colonne)
      const getMeterLabel = (m: DiagnosisMetersReadDTO) => {
        const meterCode = m.meter_code || '';
        const customerCode = (m as any).meter_customer_code || '';
        const codeDisplay = customerCode ? `${meterCode}-${customerCode}` : meterCode;
        const udm = m.unit_of_measurement
          ? ` [${formatUnitOfMeasurement(m.unit_of_measurement)}]`
          : '';
        return `${codeDisplay || '-'}${udm}`;
      };
      const getVariableLabel = (v: DiagnosisRelevantVariablesReadDTO) => {
        const variable = (v as any).variable || '';
        const tableCode = v.table_code_index || '';
        const udm = v.unit_of_measurement
          ? ` [${formatUnitOfMeasurement(v.unit_of_measurement)}]`
          : '';
        return `${variable}${variable ? ' - ' : ''}${tableCode}${udm}`.trim() || '-';
      };

      // Header personalizzato per la colonna dei consumi (collapsed state)
      const consumptionDateColumnHeader = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600}>
              Informazioni consumi
            </Typography>
          </Stack>
        );
      }, []);

      // Header personalizzato per la colonna dei consumi (expanded state)
      const consumptionDateColumnHeaderExpanded = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600} sx={{ pt: '0.2rem' }}>
              Informazioni consumi
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Codice
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Tipo
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Unità di misura
            </Typography>
            <Box sx={{ height: '24px' }}></Box>
          </Stack>
        );
      }, []);

      // Header personalizzato per le colonne dei consumi (collapsed state)
      const renderConsumptionHeader = React.useCallback(
        (
          consumption: DiagnosisConsumptionReadDTO,
          monthlyValues: { month: Date; value: number | string | null }[]
        ) => {
          const unitOfMeasurement = formatUnitOfMeasurement(
            (consumption as any).unit_of_measurement
          );
          return (
            <Box sx={{ my: '4px' }}>
              <Typography
                variant="caption"
                fontWeight={600}
                noWrap
                title={consumption.code || ''}
                sx={{ textAlign: 'center' }}
              >
                {consumption.code || '-'}
                {unitOfMeasurement && ` [${unitOfMeasurement}]`}
              </Typography>
            </Box>
          );
        },
        []
      );

      // Funzioni per gestire edit e delete dei consumi
      const handleEditConsumptionClick = React.useCallback(
        (id: GridRowId) => {
          const consumption = allConsumptions.find(c => c.id === id);
          if (consumption) {
            setSelectedConsumption(consumption);
            setEditConsumptionDialogOpen(true);
          }
        },
        [allConsumptions]
      );

      const handleDeleteConsumptionClick = React.useCallback((id: GridRowId) => {
        setDeleteConsumptionDialogState({ open: true, id: id as string });
      }, []);

      const deleteConsumptionMutation = useMutation(
        async (id: string) => {
          await apiClient.delete_item_diagnosis_consumption__item_id__delete(id);
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
            invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
            toast.success('Consumo eliminato con successo');
            setDeleteConsumptionDialogState({ open: false, id: null });
          },
          onError: (error: any) => {
            toast.error(error?.message || 'Eliminazione del consumo non riuscita');
          },
        }
      );

      // Header personalizzato per le colonne dei consumi (expanded state)
      const renderConsumptionHeaderExpanded = React.useCallback(
        (
          consumption: DiagnosisConsumptionReadDTO,
          monthlyValues: { month: Date; value: number | string | null }[]
        ) => {
          const typeValue = consumption.type as string;
          const typeLabel =
            typeValue === ConsumptionType.Total || typeValue === 'total'
              ? 'Totale'
              : typeValue === ConsumptionType.Withdrawn || typeValue === 'withdrawn'
                ? 'Prelevata'
                : typeValue === ConsumptionType.Self_consumed || typeValue === 'self_consumed'
                  ? 'Autoconsumata'
                  : typeValue || '-';
          return (
            <Stack spacing={0.5} sx={{ fontSize: '0.7rem', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleEditConsumptionClick(consumption.id as any)}
                >
                  <EditIcon fontSize="small" />
                </Box>
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleDeleteConsumptionClick(consumption.id as any)}
                >
                  <DeleteIcon fontSize="small" />
                </Box>
              </Box>
              <Typography variant="caption" fontWeight={600} noWrap>
                {consumption.code || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {typeLabel}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {formatUnitOfMeasurement((consumption as any).unit_of_measurement || '-')}
              </Typography>
            </Stack>
          );
        },
        [handleEditConsumptionClick, handleDeleteConsumptionClick]
      );

      const saveTotalsChanges = React.useCallback(async (): Promise<boolean> => {
        const initialMap = new Map<string, ConsumptionCostData | undefined>();
        initialValuesRef.current.forEach(item => {
          const key = `${item.entityId}-${dayjs(item.date).year()}`;
          initialMap.set(key, item.value);
        });

        const changes: Array<
          | { type: 'update'; id: string; data: ConsumptionTotalsCreateDTO }
          | { type: 'create'; data: ConsumptionTotalsCreateDTO }
        > = [];

        valuesData.forEach(item => {
          const year = dayjs(item.date).year();
          const key = `${item.entityId}-${year}`;
          const currentValue = item.value;
          if (!currentValue || !currentValue.diagnosis_consumption_id) {
            return;
          }
          const initialValue = initialMap.get(key);

          const normalize = (value: number | null | undefined) =>
            value === null || value === undefined ? null : Number(value);

          const currentPrice = normalize(currentValue.taxable_amount);
          const currentUnitCost = normalize(currentValue.unit_cost);
          const initialPrice = normalize(initialValue?.taxable_amount);
          const initialUnitCost = normalize(initialValue?.unit_cost);

          const changed = currentPrice !== initialPrice || currentUnitCost !== initialUnitCost;

          if (!changed) {
            return;
          }

          const dto = new ConsumptionTotalsCreateDTO();
          dto.diagnosis_consumption_id = currentValue.diagnosis_consumption_id!;
          dto.year = currentValue.year ?? year;
          dto.price = currentValue.taxable_amount ?? undefined;
          dto.unit_cost = currentValue.unit_cost ?? undefined;

          if (currentValue.id) {
            changes.push({ type: 'update', id: currentValue.id, data: dto });
          } else {
            changes.push({ type: 'create', data: dto });
          }
        });

        if (!changes.length) {
          return false;
        }

        for (const change of changes) {
          if (change.type === 'update') {
            await updateConsumptionTotal({ id: change.id, data: change.data });
          } else {
            await createConsumptionTotal(change.data);
          }
        }

        return true;
      }, [valuesData, createConsumptionTotal, updateConsumptionTotal]);

      React.useImperativeHandle(
        ref,
        () => ({
          save: async () => {
            if (!consumptionGridRef.current) {
              return { success: false, error: 'Riferimento alla tabella non disponibile' };
            }

            // Save order if changed (compare with original order)
            const currentOrder = consumptionGridRef.current.getEntityOrder();
            const originalOrder = allConsumptions.map(c => c.id!).filter(Boolean);
            const orderChanged =
              currentOrder.length === originalOrder.length &&
              currentOrder.some((id, index) => id !== originalOrder[index]);

            if (orderChanged) {
              try {
                await updateOrderMutation.mutateAsync(currentOrder);
                // Invalidate consumptions query first to get the new order, then invalidate and refetch totals
                await queryClient.invalidateQueries({
                  queryKey: [`all-consumptions-${diagnosisId}`],
                });
                invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
                await refetchConsumptionTotals();
              } catch (error) {
                // Error handled by mutation's onError
                return { success: false };
              }
            }

            if (!crossEntityGridRef.current) {
              return { success: false, error: 'Riferimento alla tabella non disponibile' };
            }

            try {
              crossEntityGridRef.current.getTableData();
              const hasSavedTotals = await saveTotalsChanges();
              const hasSavedRefPeriod = await saveReferencePeriodChanges();
              if (hasSavedTotals) {
                await refetchConsumptionTotals();
                await queryClient.invalidateQueries({
                  queryKey: [`all-consumptions-${diagnosisId}`],
                });
              }
              if (hasSavedRefPeriod) {
                await queryClient.invalidateQueries({
                  queryKey: [`all-consumptions-${diagnosisId}`],
                });
                // Invalida i dati che dipendono dal periodo di riferimento (bilancio, impieghi, aree funzionali, ENEA)
                queryClient.invalidateQueries({
                  queryKey: ['diagnosis-energy-balance', diagnosisId],
                });
                queryClient.invalidateQueries({
                  queryKey: ['diagnosis-incidence-enea', diagnosisId],
                });
                queryClient.invalidateQueries({
                  queryKey: [`diagnosis-energy-use-vector-data-${diagnosisId}`],
                });
                queryClient.invalidateQueries({
                  queryKey: [`diagnosis-energy-use-totals-data-${diagnosisId}`],
                });
                queryClient.invalidateQueries({
                  queryKey: ['consumption-energy-vector-functional-area'],
                });
                // Invalida anche i censimenti, perché % sul consumo totale e costo annuo
                // dipendono dai valori reference_period_* del consumo totale.
                queryClient.invalidateQueries(['census-code-a']);
                queryClient.invalidateQueries(['census-code-b']);
                queryClient.invalidateQueries(['census-code-c']);
              }
              if (hasSavedTotals || hasSavedRefPeriod) {
                toast.success('Dati di costo salvati con successo');
              } else if (!orderChanged) {
                toast.success('Nessuna modifica da salvare');
              }
              return { success: true };
            } catch (error: any) {
              toast.error(error?.message || 'Errore durante il salvataggio');
              return { success: false, error: error?.message };
            }
          },
        }),
        [
          saveTotalsChanges,
          saveReferencePeriodChanges,
          refetchConsumptionTotals,
          allConsumptions,
          updateOrderMutation,
          queryClient,
          diagnosisId,
        ]
      );

      if (!diagnosisId) {
        return (
          <Typography color="text.secondary">
            Salva le informazioni principali della diagnosi per gestire i consumi e i costi.
          </Typography>
        );
      }

      if (!hasGeneratedPeriod) {
        return <Box sx={{ mt: 2 }}>Selezionare un periodo dati generali</Box>;
      }

      if (energyVectors.length === 0) {
        return (
          <Typography color="text.secondary">
            Aggiungi almeno un vettore energetico per gestire i consumi e i costi.
          </Typography>
        );
      }

      return (
        <>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <Typography variant="h6">Consumi e costi</Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, v) => v != null && setViewMode(v)}
                size="small"
                color="primary"
              >
                <ToggleButton value="table" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TableRowsIcon fontSize="small" />
                  <Typography variant="body2">Tabella</Typography>
                </ToggleButton>
                <ToggleButton value="charts" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <ShowChartIcon fontSize="small" />
                  <Typography variant="body2">Grafici</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack spacing={2} sx={{ mt: 3, display: viewMode === 'table' ? 'block' : 'none' }}>
              {hasGeneratedPeriod && allConsumptions.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Fabbisogni mensili di tutti i vettori energetici
                  </Typography>
                  <Box sx={{ width: '100%' }}>
                    <CrossEntityDateCrudDataGrid
                      ref={consumptionGridRef}
                      periodFrom={diagnosis?.data_generated_from!}
                      periodTo={diagnosis?.data_generated_to!}
                      entities={allConsumptions}
                      values={consumptionDataValues}
                      getEntityLabel={consumption => consumption.code || consumption.id || ''}
                      getValue={valueObj => valueObj.value ?? null}
                      setValue={(valueObj, newValue) => {
                        const updated = { ...valueObj };
                        if (updated.value) {
                          updated.value = {
                            ...updated.value,
                            value:
                              newValue !== null && newValue !== '' ? Number(newValue) : undefined,
                          };
                        }
                        return updated as any;
                      }}
                      dateColumnHeader={consumptionDateColumnHeader}
                      dateColumnHeaderExpanded={consumptionDateColumnHeaderExpanded}
                      renderEntityHeader={renderConsumptionHeader}
                      renderEntityHeaderExpanded={renderConsumptionHeaderExpanded}
                      entityColumnWidth={120}
                      entityColumnWidthExpanded={200}
                      widthCol={250}
                      widthColDate={300}
                      height="calc(100vh - 500px)"
                      loading={
                        isLoadingVectors ||
                        isLoadingConsumptions ||
                        isLoadingConsumptionData ||
                        updateOrderMutation.isLoading
                      }
                      readOnly={true}
                      syncScrollLeft={horizontalScrollLeft}
                      onHorizontalScroll={setScrollPos}
                    />
                  </Box>
                </Box>
              )}

              <Box sx={{ pt: '1rem', pb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Dati di consumo energetico, di produzione e di costo - valori annui
                </Typography>
                <Box sx={{ width: '100%' }}>
                  <CrossEntityDateCrudDataGrid
                    ref={crossEntityGridRef}
                    granularity="year"
                    periodFrom={diagnosis?.data_generated_from!}
                    periodTo={diagnosis?.data_generated_to!}
                    entities={allConsumptions}
                    values={valuesData}
                    valuesProps={valuesProps}
                    getEntityLabel={consumption => consumption.code || consumption.id || ''}
                    dateColumnLabel="Anno"
                    height={470}
                    loading={
                      isLoadingVectors ||
                      isLoadingConsumptions ||
                      isLoadingConsumptionData ||
                      isLoadingConsumptionTotals ||
                      updateOrderMutation.isLoading
                    }
                    enablePaste={!readOnly}
                    readOnly={readOnly}
                    renderDataColumnCell={renderDataColumnCell}
                    renderCell={renderConsumptionValueCell}
                    renderEntityHeader={renderConsumptionHeader}
                    renderEntityHeaderExpanded={renderConsumptionHeader}
                    processRowUpdate={processRowUpdate}
                    dateColumnHeader={<Box sx={{ height: '100%' }}>Anno</Box>}
                    widthColDate={140}
                    widthCol={250}
                    disableHeaderExpand={true}
                    syncScrollLeft={horizontalScrollLeft}
                    onHorizontalScroll={setScrollPos}
                  />
                </Box>
              </Box>

              {referencePeriodStart != null && referencePeriodEnd != null && (
                <Box sx={{ pt: '1rem', pb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Periodo di riferimento per la diagnosi energetica
                  </Typography>
                  <Box sx={{ width: '100%' }}>
                    <CrossEntityDateCrudDataGrid
                      ref={crossEntityGridRefPeriodoRiferimento}
                      granularity="year"
                      periodFrom={referencePeriodStart}
                      periodTo={referencePeriodEnd}
                      entities={allConsumptions}
                      values={
                        refPeriodValues.length > 0 ? refPeriodValues : valuesDataReferencePeriod
                      }
                      valuesProps={valuesProps}
                      getEntityLabel={consumption => consumption.code || consumption.id || ''}
                      dateColumnLabel="Periodo"
                      height={150}
                      loading={
                        isLoadingVectors ||
                        isLoadingConsumptions ||
                        isLoadingConsumptionData ||
                        isLoadingConsumptionTotals ||
                        updateOrderMutation.isLoading
                      }
                      enablePaste={true}
                      renderDataColumnCell={renderDataColumnCell}
                      renderCell={renderConsumptionValueCell}
                      renderEntityHeader={renderConsumptionHeader}
                      renderEntityHeaderExpanded={renderConsumptionHeader}
                      processRowUpdate={processRowUpdateRefPeriod}
                      dateColumnHeader={<Box sx={{ height: '100%' }}>Periodo</Box>}
                      disableHeaderExpand={true}
                      widthColDate={140}
                      widthCol={250}
                      syncScrollLeft={horizontalScrollLeft}
                      onHorizontalScroll={setScrollPos}
                    />
                  </Box>
                </Box>
              )}
              <ActionSpacer></ActionSpacer>
            </Stack>

            {/* Sezione Grafici: componente esterno con grafici standard e grafico personalizzabile */}
            <Box sx={{ display: viewMode === 'charts' ? 'block' : 'none' }}>
              <ConsumptionCostsChartsSection
                diagnosis={diagnosis ?? null}
                referencePeriodFrom={
                  diagnosis?.data_generated_from != null
                    ? typeof diagnosis?.data_generated_from === 'string'
                      ? diagnosis?.data_generated_from
                      : new Date(diagnosis?.data_generated_from).toISOString().slice(0, 10)
                    : null
                }
                referencePeriodTo={
                  diagnosis?.data_generated_to != null
                    ? typeof diagnosis?.data_generated_to === 'string'
                      ? diagnosis?.data_generated_to
                      : new Date(diagnosis?.data_generated_to).toISOString().slice(0, 10)
                    : null
                }
                allConsumptions={allConsumptions}
                consumptionDataValues={consumptionDataValues}
                consumptionTotals={consumptionTotals}
                metersFiltered={metersFiltered}
                allMetersData={allMetersData}
                relevantVariables={relevantVariables}
                allVariablesData={allVariablesData}
                energyVectors={energyVectors}
              />
            </Box>
          </Stack>

          {/* Dialog per modificare i consumi */}
          {selectedConsumption && (
            <EditConsumptionDialog
              open={editConsumptionDialogOpen}
              onClose={() => {
                setEditConsumptionDialogOpen(false);
                setSelectedConsumption(null);
              }}
              consumption={selectedConsumption}
              energyVectorId={selectedConsumption.diagnosis_energy_vector_id || ''}
              diagnosisId={diagnosisId}
              existingConsumptions={allConsumptions.filter(
                c => c.diagnosis_energy_vector_id === selectedConsumption.diagnosis_energy_vector_id
              )}
              genericEnergyVectors={genericEnergyVectors}
            />
          )}

          {/* Dialog di conferma eliminazione consumo */}
          <ConfirmDialog
            open={deleteConsumptionDialogState.open}
            onCancel={() => setDeleteConsumptionDialogState({ open: false, id: null })}
            onConfirm={() => {
              if (deleteConsumptionDialogState.id) {
                deleteConsumptionMutation.mutate(deleteConsumptionDialogState.id);
              }
            }}
            title="Conferma eliminazione"
            content="Sei sicuro di voler eliminare questo consumo?"
            confirmText="Elimina"
            cancelText="Annulla"
            loading={deleteConsumptionMutation.isLoading}
          />
        </>
      );
    }
  )
);

DiagnosisConsumptionCostsStep.displayName = 'DiagnosisConsumptionCostsStep';
